import { useFaultStudyCombined } from '../../hooks/useFaultAnalysis.ts'
import { AsOfStamp } from './AsOfStamp.tsx'
import { PPE_CATEGORY_COLORS } from '../../types/faultAnalysis.ts'
import { collidingNames, disambiguatedName } from '../../utils/deviceNames.ts'
import { clearingSourceLabel, isEstimatedClearing } from '../../utils/faultLabels.ts'
import {
  IndicativeBanner,
  INDICATIVE_VALUE_COLOR,
  NEUTRAL_BADGE_CLASS,
  SetupDataChip,
} from './indicative.tsx'
import { Term } from '../common/Term.tsx'
import { AssumptionsDisclosure } from './AssumptionsDisclosure.tsx'
import { friendlyError } from '../../utils/friendlyError.ts'

interface ArcFlashSummaryProps {
  feederId: number
}

const PPE_BADGES: Record<string, string> = {
  'No PPE Required': 'bg-green-100 text-green-700',
  'Cat 1': 'bg-blue-100 text-blue-700',
  'Cat 2': 'bg-yellow-100 text-yellow-800',
  'Cat 3': 'bg-orange-100 text-orange-700',
  'Cat 4': 'bg-red-100 text-red-700',
  'DANGEROUS': 'bg-purple-100 text-purple-800',
}

export function ArcFlashSummary({ feederId }: ArcFlashSummaryProps) {
  const { data, isLoading, error } = useFaultStudyCombined(feederId, r => r.arc_flash)

  if (isLoading) {
    return <div className="text-sm text-gray-400 p-4">Calculating arc flash hazard...</div>
  }

  if (error) {
    return (
      <div className="text-sm text-red-600 p-4">
        {friendlyError(error, 'load arc flash data')}
      </div>
    )
  }

  if (!data) {
    return <div className="text-sm text-gray-400 p-4">No arc flash data available</div>
  }

  // C8: names borne by 2+ devices in this result set get " · #<id>" appended.
  const colliding = collidingNames(
    data.results.map(r => ({ name: r.device_name, id: r.device_id })),
  )

  // INDICATIVE ONLY lines are dropped here the same way WarningSummary drops
  // them from warnings (spec §3.1 drop rule) — the IndicativeBanner above
  // already carries that message in plain English, so the raw jargon line
  // is pure duplication.
  const visibleDisclaimers = data.disclaimers.filter(d => !/INDICATIVE ONLY/.test(d))

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">Arc Flash Hazard Analysis</h3>
        <span className="text-xs text-gray-500">{data.total_locations} locations</span>
      </div>
      <AsOfStamp computedAt={data.analysis_timestamp} />

      {data.indicative_only && <IndicativeBanner />}

      {/* Screening-level estimate banner */}
      <div className="rounded-lg border-2 border-orange-300 bg-orange-50 px-4 py-3">
        <div className="flex items-start gap-2">
          <span className="text-orange-500 text-lg leading-none mt-0.5">!</span>
          <div>
            <div className="text-sm font-semibold text-orange-800">Screening-Level Estimate Only</div>
            <div className="text-xs text-orange-700 mt-0.5">
              These results are for preliminary screening purposes only and must not be used as the
              sole basis for PPE selection. A detailed arc flash study by a qualified engineer is
              required for compliance with NFPA 70E.
            </div>
          </div>
        </div>
      </div>

      {/* Estimated clearing time warning */}
      {data.estimated_clearing_time_count > 0 && (
        <div className="rounded border border-yellow-300 bg-yellow-50 px-3 py-2">
          <div className="text-xs text-yellow-800">
            <span className="font-semibold">{data.estimated_clearing_time_count}</span> of{' '}
            {data.total_locations} locations use estimated clearing times. Actual device
            coordination data should be used for final analysis.
          </div>
        </div>
      )}

      {/* Warnings render once per tab via WarningSummary (spec §3.1). */}

      {/* A3 (spec §3.5): the modelling assumptions this run actually used. */}
      <AssumptionsDisclosure assumptions={data.assumptions ?? []} />

      {/* PPE category distribution. Chips grey out while indicative (A1). */}
      <div>
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
          PPE Category Distribution
        </h4>
        <div className="flex flex-wrap gap-2">
          {(
            [
              ['DANGEROUS', 'DANGEROUS', data.dangerous_count],
              ['Cat 4', 'Cat 4', data.cat4_count],
              ['Cat 3', 'Cat 3', data.cat3_count],
              ['Cat 2', 'Cat 2', data.cat2_count],
              ['Cat 1', 'Cat 1', data.cat1_count],
              ['No PPE Required', 'No PPE', data.no_ppe_count],
            ] as const
          )
            .filter(([, , count]) => count > 0)
            .map(([cat, label, count]) => {
              const color = data.indicative_only
                ? INDICATIVE_VALUE_COLOR
                : PPE_CATEGORY_COLORS[cat]
              return (
                <span
                  key={cat}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium"
                  style={{ backgroundColor: color + '20', color }}
                >
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
                  {label}: {count}
                </span>
              )
            })}
        </div>
      </div>

      {/* Methodology label */}
      <div className="text-xs text-gray-500">
        Methodology: <span className="font-medium">{data.methodology}</span>
      </div>

      {/* Results table */}
      {data.results.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
            Results ({data.results.length})
            {data.indicative_only && <SetupDataChip />}
          </h4>
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b border-gray-200 text-left text-gray-500">
                  <th className="pb-1 pr-2">Device</th>
                  <th className="pb-1 pr-2 text-right">Bolted (kA)</th>
                  <th className="pb-1 pr-2 text-right">Arcing (kA)</th>
                  <th className="pb-1 pr-2 text-right"><Term k="calcm2">Energy (cal/cm2)</Term></th>
                  <th className="pb-1 pr-2"><Term k="ppe">PPE Category</Term></th>
                  <th className="pb-1 pr-2 text-right">Clearing (s)</th>
                  <th className="pb-1">Source</th>
                </tr>
              </thead>
              <tbody>
                {data.results.map((r, i) => {
                  // A1: a defaulted clearing time makes the energy/PPE numbers
                  // placeholders — grey those two cells with a tooltip even
                  // when the study is otherwise real. Indicative greys all
                  // magnitude cells.
                  const estimated = isEstimatedClearing(r.clearing_time_source)
                  const greyEnergy = data.indicative_only || estimated
                  const estimatedTitle = estimated
                    ? 'Clearing time is an assumed default — energy and PPE category are placeholders until device clearing data is loaded'
                    : undefined
                  const badgeClass = greyEnergy
                    ? NEUTRAL_BADGE_CLASS
                    : PPE_BADGES[r.ppe_category] || 'bg-gray-100 text-gray-600'
                  const muted = data.indicative_only ? ' indicative-muted' : ''
                  return (
                    <tr key={i} className="border-b border-gray-50">
                      <td className="py-1 pr-2">
                        <div className="text-gray-700 font-medium">
                          {disambiguatedName(r.device_name, r.device_id, colliding) || r.node_name}
                        </div>
                        {r.device_type && (
                          <div className="text-gray-400 capitalize">{r.device_type.replace(/_/g, ' ')}</div>
                        )}
                      </td>
                      <td className={`py-1 pr-2 text-right text-gray-800 font-medium${muted}`}>
                        {r.bolted_fault_current_ka.toFixed(2)}
                      </td>
                      <td className={`py-1 pr-2 text-right text-gray-600${muted}`}>
                        {r.arcing_current_ka.toFixed(2)}
                      </td>
                      <td
                        className={`py-1 pr-2 text-right font-medium${greyEnergy ? ' indicative-muted' : ''}`}
                        style={greyEnergy ? undefined : { color: PPE_CATEGORY_COLORS[r.ppe_category] || '#374151' }}
                        title={estimatedTitle}
                      >
                        {r.incident_energy_cal_cm2.toFixed(1)}
                      </td>
                      <td className="py-1 pr-2" title={estimatedTitle}>
                        <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium ${badgeClass}`}>
                          {r.ppe_category}
                        </span>
                      </td>
                      <td className={`py-1 pr-2 text-right text-gray-600${muted}`}>
                        {r.clearing_time_sec.toFixed(3)}
                      </td>
                      <td className="py-1 text-gray-500">
                        {clearingSourceLabel(r.clearing_time_source)}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {data.results.length === 0 && (
        <div className="text-sm text-gray-400 p-2">No arc flash results for this feeder</div>
      )}

      {/* Disclaimers */}
      {visibleDisclaimers.length > 0 && (
        <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 space-y-1">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Disclaimers</h4>
          {visibleDisclaimers.map((d, i) => (
            <div key={i} className="text-xs text-gray-500 italic">{d}</div>
          ))}
        </div>
      )}
    </div>
  )
}
